import {
  signInWithRedirect,
  getRedirectResult,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  updateProfile,
  EmailAuthProvider,
  reauthenticateWithCredential,
  signOut as firebaseSignOut,
  GoogleAuthProvider,
  OAuthProvider,
  onAuthStateChanged,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { auth, db } from './config';

// Google Auth provider
const googleProvider = new GoogleAuthProvider();
googleProvider.addScope('email');
googleProvider.addScope('profile');

// Microsoft Auth provider (OIDC)
const microsoftProvider = new OAuthProvider('microsoft.com');
microsoftProvider.addScope('email');
microsoftProvider.addScope('profile');
microsoftProvider.addScope('openid');

export const authService = {
  // Google auth using redirect (more reliable across browsers, avoids COOP issues)
  signInWithGoogle: async (): Promise<void> => {
    await signInWithRedirect(auth, googleProvider);
  },

  // Microsoft auth using redirect (works with custom domain as authDomain)
  signInWithMicrosoft: async (): Promise<void> => {
    await signInWithRedirect(auth, microsoftProvider);
  },

  // Handle redirect result after OAuth sign-in (Google or Microsoft)
  getRedirectResult: async (): Promise<User | null> => {
    const result = await getRedirectResult(auth);
    return result?.user || null;
  },

  // Email/password authentication
  signInWithEmail: async (email: string, password: string): Promise<User> => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  },

  signUpWithEmail: async (email: string, password: string): Promise<User> => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    return result.user;
  },

  resetPassword: async (email: string): Promise<void> => {
    await sendPasswordResetEmail(auth, email);
  },

  signOut: async (): Promise<void> => {
    await firebaseSignOut(auth);
  },

  getCurrentUser: (): User | null => {
    return auth.currentUser;
  },

  onAuthStateChanged: (callback: (user: User | null) => void) => {
    return onAuthStateChanged(auth, callback);
  },

  getIdToken: async (): Promise<string | null> => {
    const user = auth.currentUser;
    if (!user) return null;
    return user.getIdToken();
  },

  // Change password (requires re-authentication for security)
  changePassword: async (currentPassword: string, newPassword: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user || !user.email) {
      throw new Error('No user signed in or user has no email');
    }

    // Re-authenticate user before changing password
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);

    // Update password
    await updatePassword(user, newPassword);
  },

  // Check if user signed in with email/password (vs OAuth)
  isEmailUser: (): boolean => {
    const user = auth.currentUser;
    if (!user) return false;
    return user.providerData.some(provider => provider.providerId === 'password');
  },

  // Update user profile (display name)
  updateUserProfile: async (displayName: string): Promise<void> => {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('No user signed in');
    }
    await updateProfile(user, { displayName });
  },

  // Create or update user profile in Firestore
  // Called when user signs in to ensure they have a profile document
  ensureUserProfile: async (user: User): Promise<void> => {
    const userRef = doc(db, 'users', user.uid);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      // Create new profile for first-time user
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        plan: 'free',
        createdAt: serverTimestamp(),
        lastLoginAt: serverTimestamp(),
      });
    } else {
      // Update last login time for existing user
      await updateDoc(userRef, {
        lastLoginAt: serverTimestamp(),
        // Update these in case they changed (e.g., user updated their Google profile)
        displayName: user.displayName,
        photoURL: user.photoURL,
      });
    }
  },
};
